import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { Role } from '../../model/auth/Role';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { EnrolData, User, UserExistsBaseDto } from '../../model/auth/User';
import {
  InviteUserDetailsData,
  InviteUserModalComponent,
  InviteUserResult
} from '../../profile/invite-user-modal/invite-user-modal.component';
import { Contest } from '../../model/contest/Contest';
import { TenantService } from '../../service/tenant.service';
import { ContestService } from '../../service/contest.service';
import { AuthService } from '../../service/auth.service';
import { School, SchoolType } from '../../model/location/School';
import { NotificationHandler } from '../../helper/notification.handler';
import { LocationService } from '../../service/location.service';
import { Region } from '../../model/location/Region';
import { Page } from '../../model/Page';
import {
  ContestEnrollmentAwareComponent
} from '../../contest/contest-enrollment-aware/contest-enrollment-aware.component';
import { TrackingService } from '../../service/tracking.service';
import { GROUP_PAYMENT_OBJECT_TYPE, PaymentStatus } from '../../model/Payment';
import { SchoolSelectResult } from '../../profile/school-select/school-select.component';
import {
  ConfirmationContext,
  ConfirmationData,
  ConfirmationModalComponent
} from '../../common/modal/confirmation-modal/confirmation-modal.component';
import { Competition } from '../../model/competition/Competition';
import {
  ImportUsersModalComponent,
  ImportUsersModalData
} from '../../profile/import-users-modal/import-users-modal.component';
import { FormControl, Validators } from '@angular/forms';
import { UserService } from '../../service/user.service';
import {
  UserDetails,
  UserSelectChecker,
  UserSelectionModel,
  UsersTableComponent
} from '../../profile/users-table/users-table.component';
import {
  SelectTeacherModalComponent,
  TeacherSelectData
} from '../../teacher/select-teacher-modal/select-teacher-modal.component';
import {
  ChangePassResultModalComponent,
  ChangePassResultResult
} from '../../profile/change-pass-result-modal/change-pass-result-modal.component';

@Component({
  selector: 'app-students-list',
  templateUrl: './students-list.component.html',
  styleUrls: ['./students-list.component.scss']
})
export class StudentsListComponent extends ContestEnrollmentAwareComponent implements OnInit {

  readonly STUDENT_ROLE = Role.Student;

  readonly roles = [Role.Host, Role.Coordinator, Role.Teacher];

  inProgress = false;

  private tenant?: Competition | null;

  students!: User[] | null;
  hasLocallyAdded = false;

  competitionId: string | undefined;
  contest!: Contest;
  contestOpenForEnrol = false;

  selectedForPayment?: User[];
  freeEnrollAllowed = false;

  deleteAllowed = false;
  deletePaidAllowed = false;
  usernameAllowed = false;
  showContestProgress = false;

  region?: Region;
  allowRegionSelect = false;

  schoolType?: SchoolType;
  school?: School | null;
  allowSchoolSelect = false;

  get selectionChoices(): UserSelectionModel[] {
    const model: UserSelectionModel[] = [];

    if (!!this.contest?.enrolEndsAt && this.contest.enrolEndsAt > new Date()) {
      model.push({
        title: $localize`:Users List|Payment selection mode title@@users-list-payment-selection-mode-title:Group Payments`,
        type: 'payment',
        cta_title: $localize`:Students List|Pay For Selected button title@@students-list-pay-for-selected-button-title:Pay For Selected`,
        checker: user => user.paymentStatus == PaymentStatus.scheduled || user.paymentStatus == PaymentStatus.failed,
        highlighter: user => user.paymentStatus != PaymentStatus.scheduled && user.paymentStatus != PaymentStatus.failed
      });
    }

    if (this.region && this.school) {
      model.push({
        title: $localize`:Select Teacher|Teacher selection title@@select-teacher-selection-title:Teacher Selection`,
        type: 'teacher',
        cta_title: $localize`:General|Select action label@@general-select-action-label:Select`,
        checker: () => true,
        highlighter: user => !!user.teacher_id,
      });
    }

    if (this.usernameAllowed) {
      model.push({
        title: $localize`:i18n="Students List|Get Passwords button title@@students-list-get-passwords-button-title:Get Passwords`,
        type: 'passwords',
        cta_title: $localize`:i18n="Students List|Get Passwords button title@@students-list-get-passwords-button-title:Get Passwords`,
        checker: user => !user.email && !!user.username,
        highlighter: user => !!user.email || user.status != 'FORCE_CHANGE_PASSWORD',
      });
    }

    return model;
  }

  userSelectAllowed: UserSelectChecker = (user) => {
    return user.paymentStatus == PaymentStatus.scheduled || user.paymentStatus == PaymentStatus.failed;
  };

  identifierField = new FormControl('', [
    Validators.minLength(5), Validators.maxLength(128)
  ]);
  user?: UserExistsBaseDto;

  page: Page<never> = {
    max_items: 20,
    asc: true
  };

  constructor(private contestService: ContestService,
              private authService: AuthService,
              private userService: UserService,
              private locationService: LocationService,
              private tenantService: TenantService,
              private dialog: MatDialog,
              trackingService: TrackingService,
              ngZone: NgZone,
              breakpointObserver: BreakpointObserver) {
    super(trackingService, ngZone, breakpointObserver, dialog);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.tenant = this.tenantService.getTenant();
  }

  async initContent() {
    if (this.tenant && this.tenant.id) {
      this.competitionId = this.tenant.id;
    }

    const user = await this.authService.getUser();
    if (user && user.region) this.region = new Region(user.region, '');
    if (user && user.schoolId) {
      this.loadSchool(user.schoolId);
    } else {
      this.school = null;
    }
  }

  get disablePaymentCheckBeforeDelete(): boolean {
    return this.deletePaidAllowed
      && !!this.contest
      && !!this.contest.startsAt
      && this.contest.startsAt > new Date()
      && this.contest.price == 0;
  }

  callerRolesUpdated(roles: Role[]) {
    this.allowSchoolSelect = roles.includes(Role.Host) || roles.includes(Role.Coordinator);
    this.allowRegionSelect = roles.includes(Role.Host);
    this.freeEnrollAllowed = roles.includes(Role.Host);
    this.deleteAllowed = roles.includes(Role.Host) || roles.includes(Role.Coordinator) || roles.includes(Role.Teacher);
    this.deletePaidAllowed = roles.includes(Role.Host);
    this.usernameAllowed = this.tenant?.domain == 'movoznavets';
    this.showContestProgress = roles.includes(Role.Host);
  }

  loadSchool(schoolId: string) {
    this.locationService.getSchoolDetails(schoolId).subscribe({
      next: school => {
        this.school = school;
      },
      error: error => {
        NotificationHandler.handleError($localize`:Notifications|School details fetch has failed message@@notifications-school-details-fetch-failed-error:School details fetch has failed`, error);
      }
    });
  }

  loadPendingGroupPending(contest: Contest) {
    if (this.competitionId && this.region) {
      const schoolId = this.school ? this.school.id : undefined;

      this.contestService.getPayments(this.competitionId, contest.id, this.region.id,
        GROUP_PAYMENT_OBJECT_TYPE, PaymentStatus.pending, schoolId, {max_items: 1}).subscribe({
        next: page => {
          if (page && page.content && page.content.length > 0) {
            this.checkPaymentStatus(contest.id, undefined, undefined, page.content[0].id);
          }
        },
        error: error => {
          NotificationHandler.handleError($localize`:Notifications|Pending payment fetch has failed message@@notifications-pending-payment-fetch-failed-error:Pending payment fetch has failed`, error);
        }
      });
    }
  }

  contestSelected(contest: Contest) {
    this.contest = contest;
    this.loadStudents(this.competitionId, this.contest, true);
    this.loadPendingGroupPending(contest);
  }

  regionSelected(region: Region) {
    if (this.region && this.region.id != region.id) {
      this.school = null;
    }

    this.region = region;
    this.loadStudents(this.competitionId, this.contest, true);
  }

  schoolSelected(result: SchoolSelectResult) {
    const school = result && result.school ? result.school : null;
    this.schoolType = result ? result.type : undefined;

    if (this.school != school) {
      this.school = school;
      this.loadStudents(this.competitionId, this.contest, true);
      if (this.contest) this.loadPendingGroupPending(this.contest);
    }
  }

  loadStudents(competitionId?: string, contest?: Contest, reset = false) {
    if (!competitionId || !contest || !this.region) return;

    this.inProgress = true;

    if (reset) {
      this.page = {
        max_items: 20,
        asc: false
      };
      this.students = null;
      this.selectedForPayment = [];
    }

    if (this.school || this.allowSchoolSelect) {
      const userId = this.user?.id;
      const school = this.school || undefined;
      this.contestService.getEnrolledUsers(competitionId, contest.id, Role.Student, this.page, this.region, school, userId).subscribe({
        next: page => {
          const data = page.content || [];
          if (reset) {
            this.students = data;
          } else {
            if (!this.students) {
              this.students = data;
            } else {
              this.students = this.students.concat(data);
            }
          }

          this.page.next_token = page.next_token;

          this.inProgress = false;
        },
        error: error => {
          this.inProgress = false;
          NotificationHandler.handleError($localize`:Notifications|Contest students fetch has failed message@@notifications-contest-students-fetch-failed-error:Contest students fetch has failed`, error);
        }
      });
    }
  }

  contestsListUpdated(competitionId: string) {
    this.selectedForPayment = [];
    this.loadStudents(competitionId, this.contest, true);
  }

  enrolStudent(contest: Contest | null) {
    if (contest) {
      const data: InviteUserDetailsData = {
        competitionId: this.competitionId || '',
        contest: contest,

        school: this.school || undefined,
        schoolType: this.school && this.school.type ? new SchoolType(this.school.type, '') : undefined,
        region: this.region
      };

      if (this.allowSchoolSelect) {
        data.requestedRole = this.STUDENT_ROLE;
      }

      this.enrolStudentInternal(data);
    }
  }

  private enrolStudentInternal(data: InviteUserDetailsData) {
    this.dialogRef.open(InviteUserModalComponent, {
      width: '600px',
      data: data
    }).afterClosed().subscribe((data: InviteUserResult) => {
      if (data && (data.paymentPageUrl || data.exemptEnrol)) {
        this.payForContest(data.paymentPageUrl, data.exemptEnrol);
      } else if (data && data.addMore && data.addMoreData) {
        if (data.user && this.students
          && (this.region && data.user.region == this.region.id)
          && (!this.school || this.school.id == data.user.schoolId)) {
          this.students.unshift(data.user);
          this.hasLocallyAdded = true;
        }

        this.enrolStudentInternal(data.addMoreData);
      } else if (data && (data.freeEnrol || data.user) || this.hasLocallyAdded) {
        this.loadStudents(this.competitionId, this.contest, true);
      }
    });
  }

  loadNextPage() {
    this.loadStudents(this.competitionId, this.contest);
  }

  studentsSelected(users: User[], selectionModel: UserSelectionModel) {
    switch (selectionModel.type) {
      case 'payment':
        this.payForGroup(this.contest, users);
        break;
      case 'teacher':
        this.assignTeacher(users);
        break;
      case 'passwords':
        this.resetPasswords(users);
        break;
    }
  }

  assignTeacher(users: User[]) {
    if (!this.competitionId || !this.contest || !users?.length || !this.region || !this.school) return;

    const data: TeacherSelectData = {
      competitionId: this.competitionId,
      contestId: this.contest.id,
      regionId: this.region.id,
      schoolId: this.school.id,
      userIds: users.map(user => user.id),
    };

    this.dialog.open(SelectTeacherModalComponent, {
      width: '600px',
      data: data
    }).afterClosed().subscribe((users: User[]) => {
      if (users?.length) {
        this.loadStudents(this.competitionId, this.contest, true);
      }
    });
  }

  payForGroup(contest: Contest, users: User[]) {
    if (!this.region || !users || (!this.allowSchoolSelect && !this.school)) return;

    const data: EnrolData[] = [];
    users.forEach(user => data.push({
      user_id: user.id,
      role: Role.Student
    }));
    const school = !this.allowSchoolSelect
      ? this.school
        ? {id: this.school.id, name: this.school.name, short_name: this.school.short_name}
        : undefined
      : undefined;

    this.enrolGroupForContest(contest, data, this.freeEnrollAllowed, Role.Student, this.region.id, school);
  }

  paymentRetry(user: User, contest: Contest) {
    this.enrolForContest(contest, user.id, Role.Student, this.freeEnrollAllowed);
  }

  handleEnrolDelete(userDetails: UserDetails, usersTable: UsersTableComponent) {
    const data: ConfirmationData = {
      title: $localize`:Contests Details|Cancel contests enrollment modal title@@contests-details-cancel-contests-enrollment-modal-title:Cancel Contest Enrollment`,
      content: $localize`:Contests Details|Cancel student contests enrollment details@@contests-details-cancel-student-contests-enrollment-details:Do you want to cancel student's enrollment for this contest?`
    };
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.actionConfirmed.subscribe((context: ConfirmationContext) => {
      if (userDetails) {
        this.contestService.deleteUser(userDetails.competitionId, this.contest.id, this.STUDENT_ROLE, userDetails.user.id).subscribe({
          next: () => {
            if (this.students) {
              const index = this.students.findIndex(c => c.id == userDetails.user.id);
              if (index >= 0) this.students.splice(index, 1);

              usersTable.updateData();

              context.modal.proceedHandled();
            }
          },
          error: error => {
            context.modal.proceedHandled($localize`:Notifications|Contest enrollment delete has failed message@@notifications-contest-enrollment-delete-failed-error:Contest enrollment delete has failed`, error);
          }
        });
      }
    });
  }

  resetPasswords(users: User[]) {
    if (!users?.length) return;

    const data: ConfirmationData = {
      title: $localize`:Students List|Get Passwords button title@@students-list-get-passwords-button-title:Get Passwords`,
      content: $localize`:Students List|Get Passwords details@@students-list-get-passwords-details:If you proceed, all users you selected will need to use newly generated passwords. Do you want to continue?`
    };
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.actionConfirmed.subscribe((context: ConfirmationContext) => {
      context.modal.startProgress();

      const usernames = users
        .filter(user => !!user.username)
        .map(user => user.username as string);
      this.userService.changeUserPasswords(usernames, false).subscribe({
        next: results => {
          const changePassResults = new Map<string, string>();
          results.forEach(result => changePassResults.set(result.username, result.password));

          context.modal.proceedHandled();

          const data: ChangePassResultResult = {
            users,
            changePassResults,
            regions: this.region ? [this.region] : [],
          };
          this.dialog.open(ChangePassResultModalComponent, {
            width: '600px',
            data: data
          });
        },
        error: error => {
          NotificationHandler.handleError($localize`:Notifications|Change students passwords has failed message@@notifications-change-students-passwords-failed-error:Change students passwords has failed`, error);
        }
      });
    });
  }

  importStudents(competitionId: string, contest: Contest, region?: Region, school?: School | null) {
    if (!region || !school) return;

    const data: ImportUsersModalData = {
      competitionId: competitionId,
      contestId: contest.id,
      region: region,
      school: school,
    };

    this.dialog.open(ImportUsersModalComponent, {
      width: '600px',
      data: data
    }).afterClosed().subscribe(data => {
      if (data) {
        this.loadStudents(competitionId, contest, true);
      }
    });
  }

  clearUserSearch = () => {
    const refresh = !!this.user?.id;

    this.identifierField.setValue(undefined);
    this.user = undefined;
    this.identifierField.enable();

    if (refresh) this.loadStudents(this.competitionId, this.contest, true);
  };

  searchUser() {
    if (this.inProgress) return;

    const identifier = this.identifierField.value as string;
    if (identifier) {
      this.inProgress = true;
      this.identifierField.disable();
      this.userService.userExists(identifier, !identifier.includes('@')).subscribe({
        next: user => {
          this.user = user;
          this.inProgress = false;

          if (!this.user.id) {
            this.identifierField.enable();
          } else {
            this.loadStudents(this.competitionId, this.contest, true);
          }
        },
        error: error => {
          console.error(error);
          this.inProgress = false;
          this.identifierField.enable();
        }
      });
    }
  }
}

